import PropTypes from 'prop-types';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Card, Typography, Grid, IconButton, Menu, MenuItem } from '@mui/material';
import Iconify from '../../../../../reusable-components/iconify';
import LogSearchBar from '../../../../../reusable-components/log-search-bar/LogSearchBar';
import { useLogSearch, createSearchToggleHandler } from '../../../../../reusable-components/log-search-bar/logSearchUtils';
import LogSearchEntry from '../../../../../reusable-components/log-search-bar/LogSearchEntry';
import OrderLogItem from './OrderLogItem';
import Scrollbar from '../../../../../reusable-components/scrollbar/Scrollbar';
import { HOST_API_URL } from '../../../../../config-global';
import DropdownAction from '../../../../../reusable-components/dropdown-action/DropdownAction';

OrderLog.propTypes = {
  orderLogData: PropTypes.object,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  setHighlightedRow: PropTypes.func,
  isLoading: PropTypes.bool,
  orderType: PropTypes.string,
};
export default function OrderLog({ orderLogData, open, setOpen, setHighlightedRow, isLoading, orderType }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { id = '' } = useParams();
  const { orderById } = useSelector((state) => state.orders);
  const { items: orderItems = [] } = orderById[id] || {};

  const setLineHeight = () => {
    const container = document.querySelector('.logContainer');
    const line = document.querySelector('.logLine');
    if (line !== null) {
      line.style.height = `${container.scrollHeight}px`;
    }
  };

  window.addEventListener('load', setLineHeight);
  window.addEventListener('resize', setLineHeight);
  setTimeout(setLineHeight, 100);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogDownload = (e) => {
    e.stopPropagation();
    window.open(`${HOST_API_URL}Api/DownloadOrderLog?orderId=${id}`, '_blank');
    handleMenuClose();
  };

  const handleSearchToggle = createSearchToggleHandler(setShowSearchBar, setSearchQuery, () => setOpen(true));

  const handleToggleOpen = () => {
    setOpen((prevOpen) => {
      const next = !prevOpen;
      if (!next) {
        setShowSearchBar(false);
        setSearchQuery('');
      }
      return next;
    });
  };

  const logItems = orderLogData?.logItems || [];
  const { containerRef, isHidden, matchCount } = useLogSearch(searchQuery);
  const getSkuSearchText = (logItem) => {
    const orderItem = logItem.itemQuantityId ? orderItems.find((item) => item.id === logItem.itemQuantityId) : null;
    return orderItem?.vendorItemId ? [orderItem.vendorItemId] : [];
  };


  return (
    <Box sx={{ minWidth: '0', width: '100%', mb: 2 }}>
      <Card sx={{ p: 1, width: '100%' }}>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          onClick={handleToggleOpen}
          sx={{
            cursor: 'pointer',
            alignItems: 'center',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }} color="info.main">
            Order Log
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              aria-label="more"
              aria-controls={openMenu ? 'log-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openMenu ? 'true' : undefined}
              onClick={handleMenuOpen}
              size="small"
            >
              <Iconify color="info" icon="mdi:more-vert" width={20} height={20} />
            </IconButton>
            <Menu
              id="log-menu"
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleMenuClose}
              slotProps={{
                list: {
                  'aria-labelledby': 'log-menu-button',
                },
                paper: {
                  sx: {
                    width: 189,
                    p: 1,
                    borderRadius: '5px',
                    boxShadow: '0px 2px 6px rgba(0,0,0,.06), 0px 8px 24px rgba(0,0,0,.12)',
                    '& .MuiMenu-list': {
                      p: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '14px',
                    },
                  },
                },
              }}
            >
              <MenuItem
                disableRipple
                disableGutters
                sx={{
                  p: 0,
                  minHeight: 'unset',
                  '&:hover': {
                    bgcolor: 'transparent',
                  },
                }}
                onClick={handleLogDownload}
              >
                <DropdownAction badgeIcon="material-symbols:download">Download Excel</DropdownAction>
              </MenuItem>
            </Menu>
            <IconButton
              aria-label="Search log"
              onClick={handleSearchToggle}
              size="small"
              sx={{ mr: 1 }}
            >
              <Iconify color="info" icon="mdi:magnify" width={20} height={20} />
            </IconButton>
            <Typography variant="body2" sx={{ fontWeight: 'bold', cursor: 'pointer' }} color="info.main">
              <Iconify icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} height={16} />
            </Typography>
          </Box>
        </Grid>

        {showSearchBar && (
          <Box sx={{ pt: 1 }} onClick={(e) => e.stopPropagation()}>
            <LogSearchBar
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClose={handleSearchToggle}
            />
          </Box>
        )}

        {(logItems.length === 0 || (searchQuery.trim() && matchCount === 0) || isLoading) && open && (
          <Typography variant="body2" color="text.secondary">
            {isLoading ? 'Loading log items...' : searchQuery.trim() ? 'No matching log items' : 'No log items to display'}
          </Typography>
        )}
        {/* Stays mounted when nothing matches - useLogSearch reads the hidden entries' text */}
        {open && logItems.length > 0 && (
          <Scrollbar sx={{ flex: 1, maxHeight: '300px' }}>
            <Box ref={containerRef} className="logContainer" sx={{ position: 'relative', overflowX: 'hidden', overflowY: 'hidden' }}>
              <div
                className="logLine"
                style={{
                  position: 'absolute',
                  zIndex: 0,
                  left: '-6px',
                  width: '1px',
                  backgroundColor: '#208ee078',
                  margin: '20px',
                }}
              />
              {(() => {
                // idx drives the first entry's top margin, so count only entries left visible.
                let visibleIdx = -1;
                return logItems.map((logItem) => {
                  const key = `${logItem.type}-${logItem.id}`;
                  const hidden = isHidden(key);
                  if (!hidden) visibleIdx += 1;
                  return (
                    <LogSearchEntry
                      key={key}
                      entryKey={key}
                      hidden={hidden}
                      date={logItem.dateAdded}
                      searchText={getSkuSearchText(logItem)}
                    >
                      <OrderLogItem
                        logItem={logItem}
                        setHighlightedRow={setHighlightedRow}
                        idx={Math.max(visibleIdx, 0)}
                        orderType={orderType}
                      />
                    </LogSearchEntry>
                  );
                });
              })()}
            </Box>
          </Scrollbar>
        )}
      </Card>
    </Box>
  );
}